import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ElementRef,
  ViewChild,
  signal,
  OnChanges,
  SimpleChanges,
  computed,
  input,
} from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { AddNewTreatmentData } from '../add-treatment-modal.vm';
import { TreatmentTrayCardComponent } from '../treatment-tray-card';
import { AddTreatmentModalService } from '../add-treatment-modal.service';

const TRAY_GAP = 8;

@Component({
  selector: 'lib-treatment-tray',
  templateUrl: './treatment-tray.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TreatmentTrayCardComponent, SvgIconComponent, NgClass, TranslatePipe],
})
export class TreatmentTrayComponent implements OnChanges {
  private readonly addTreatmentModalService = inject(AddTreatmentModalService);

  public addedTreatments = input.required<AddNewTreatmentData[]>();
  @ViewChild('cardsContainer', { static: false }) public cardsContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('showMoreButton', { static: false }) public showMoreButton!: ElementRef<HTMLButtonElement>;

  public isTrayExpanded = signal(false);

  public readonly cardNumberInOneRow = signal(0);
  public readonly extraCardNumber = computed(() => this.addedTreatments().length - this.cardNumberInOneRow());
  public readonly treatmentsToShow = computed(() =>
    this.isTrayExpanded()
      ? this.addedTreatments()
      : this.addedTreatments().slice(0, this.addedTreatments().length - this.extraCardNumber()),
  );

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['addedTreatments']) {
      setTimeout(() => this.updateRowCount(), 0);
    }
  }

  private updateRowCount(): void {
    this.cardNumberInOneRow.set(this.calculateCardNumberInOneRow());
  }

  public onUpdateTreatment(index: number, updatedTreatment: AddNewTreatmentData): void {
    this.addTreatmentModalService.updateTreatment({ index, updatedTreatment });
  }
  public onOpenTeethSelector(index: number, treatment: AddNewTreatmentData): void {
    this.addTreatmentModalService.selectRequiredInput({ treatment, treatmentIndex: index });
  }
  public onOpenSurfaceSelector(index: number, treatment: AddNewTreatmentData, position: number): void {
    this.addTreatmentModalService.selectSurface({ treatment, selectedPositions: [position], treatmentIndex: index });
  }
  public onRemoveTreatment(treatmentIndex: number): void {
    this.addTreatmentModalService.removeTreatment(treatmentIndex);
  }

  public toggleTrayExpanded(): void {
    this.isTrayExpanded.set(!this.isTrayExpanded());
  }

  public calculateCardNumberInOneRow(): number {
    if (!this.cardsContainer) {
      return 0;
    }

    const container = this.cardsContainer.nativeElement;
    // eslint-disable-next-line unicorn/prefer-spread
    const children = Array.from(container.children) as HTMLElement[];

    const extraWidth = this.showMoreButton ? this.showMoreButton.nativeElement.offsetWidth : 0;

    return Math.floor((container.offsetWidth - extraWidth) / (children[0].offsetWidth + TRAY_GAP));
  }
}
